<template>
  <div class="text-input">
    <ValidationProvider
      v-slot="{ errors }"
      :name="label"
      :rules="validationRules"
    >
      <div
        class="vs-component vs-con-input-label vs-input w-full vs-input-primary"
      >
        <div class="vs-con-input">
          <label
            v-if="label || required"
            :for="`input-${name}`"
            class="vs-input--label mb-1"
            >{{ label }}
            <span v-if="required" class="text-danger"
              ><feather-icon icon="star-of-life"/></span
          ></label>
          <vue-select
            :id="`input-${name}`"
            v-model="internalValue"
            class="w-full data-input"
            append-to-body
            :options="options"
            :placeholder="placeholder"
            :get-option-label="getOptionLabel"
            :name="name"
            :multiple="multiple"
            :calculate-position="withPopper"
            :close-on-select="closeOnSelect"
            :clear-search-on-select="false"
            :clear-search-on-blur="clearSearchOnBlur"
            :class="{ error: errors.length }"
            :filterable="false"
            v-bind="$attrs"
            @input="onInput"
            @search="filterOptions"
            @search:focus="filterOptions"
          />
        </div>
      </div>
      <span v-show="errors.length" class="text-danger text-sm">{{
        errors[0]
      }}</span>
    </ValidationProvider>
  </div>
</template>

<script>
import VueSelect from "vue-select";
import { createPopper } from "@popperjs/core";
import gql from "graphql-tag";

export default {
  components: {
    VueSelect
  },
  props: {
    reduce: { type: Function, default: el => (el ? el.id : null) },
    initialValue: {
      type: [String, Number, Object, Array],
      default() {
        return null;
      }
    },
    value: {
      type: [String, Number, Object, Array],
      default() {
        return null;
      }
    },
    validationRules: {
      type: [Object, String],
      default() {
        return null;
      }
    },
    label: { type: String, default: null },
    modelLabel: { type: String, default: "name" },
    modelFields: { type: String, default: null },
    selectLabel: {
      type: [String, Function],
      default() {
        return this.modelLabel;
      }
    },
    primaryKey: { type: String, default: "id" },
    primaryKeyType: { type: String, default: "ID" },
    gqlQuery: { type: [String, gql, Object], default: null },
    gqlSingleQuery: { type: [String, gql, Object], default: null },
    gqlFilterType: { type: String, default: null },
    gqlQueryAttributes: { type: String, default: null },
    placeholder: { type: String, default: null },
    name: { type: String, default: null },
    multiple: { type: Boolean, default: false },
    closeOnSelect: {
      type: Boolean,
      default() {
        return !this.multiple;
      }
    },
    objectFilterAttribute: { type: String, default: "search" },
    required: { type: Boolean, default: false },
    allowNull: { type: Boolean, default: false },
    setLastElementNull: { type: Boolean, default: false },
    clearSearchOnBlur: {
      type: Function,
      default: function() {
        return true;
      }
    },
    reset: { type: Boolean, default: false }
  },
  data: () => ({
    options: [],
    internalValue: null,
    refreshTimeout: null
  }),
  watch: {
    reset(value) {
      if (value) {
        this.internalValue = null;
      }
    },
    value: {
      immediate: true,
      async handler(value) {
        if (this.multiple) {
          if (
            value instanceof Array &&
            value.length &&
            value[0] instanceof Object
          ) {
            this.internalValue = value;
            this.onInput(value);
          } else if (
            value instanceof Array &&
            value.length &&
            typeof value[0] === "string"
          ) {
            const newValue = [];
            for (const id of value) {
              const el =
                this.options.find(el => el[this.primaryKey] === id) ||
                (this.internalValue && this.internalValue.length
                  ? this.internalValue.find(el => el[this.primaryKey] === id)
                  : null);

              if (el) {
                newValue.push(el);
              } else {
                newValue.push(await this.getSingle(id));
              }
            }

            this.$emit("unreduced-input", newValue);
            this.internalValue = newValue;
          }
        } else {
          if (value === null || value[this.primaryKey] === "" || value === "") {
            this.internalValue = null;
          } else if (value instanceof Object && value[this.primaryKey] != "") {
            this.internalValue = value;
            this.onInput(value);
          } else if (value) {
            const el = this.options.find(el => el[this.primaryKey] === value);

            if (!el) {
              this.internalValue = { [this.primaryKey]: value };
              this.getSingle(value).then(r => {
                this.internalValue = r;
              });
            } else {
              this.internalValue = el;
            }
          }
        }
      }
    },
    gqlQueryAttributes() {
      this.filterOptions();
    },
    initialValue: {
      immediate: true,
      handler(v) {
        if (!this.value && v) {
          if (v instanceof Object && v[this.primaryKey] !== "") {
            this.internalValue = v;
          }
        }
      }
    }
  },
  methods: {
    async filterOptions(search, loading = () => {}) {
      let query;
      if (typeof this.gqlQuery === "object") {
        query = this.gqlQuery;
      } else {
        const qName =
          this.gqlQuery.charAt(0).toUpperCase() + this.gqlQuery.slice(1);
        query = gql`
          query FirstPage${qName} ($search: String) {
            ${this.gqlQuery}(${this.getQueryAttributes()}) {
              results {
                ${this.primaryKey}
                ${this.modelLabel}${
          this.modelFields ? "\n" + this.modelFields : ""
        }
              }
            }
          }
        `;
      }

      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(async () => {
        loading(true);

        try {
          const results = await this.$apollo.query({
            query,
            variables: {
              search: search || ""
            },
            fetchPolicy: "no-cache"
          });

          this.options = results.data[Object.keys(results.data)[0]].results;
          loading(false);
        } catch (e) {
          loading(false);
        }
      }, 400);
    },
    getQueryAttributes() {
      if (this.gqlQueryAttributes) {
        return this.gqlQueryAttributes;
      } else if (this.gqlFilterType === "objectFilter") {
        return (
          "page: 1, filter: { " + this.objectFilterAttribute + ": $search }"
        );
      }

      return "page: 1, search: $search";
    },
    async getSingle(id) {
      let query;
      if (this.gqlSingleQuery instanceof gql) {
        query = this.gqlSingleQuery;
      } else {
        let qName;
        if (this.gqlSingleQuery) {
          qName = this.gqlSingleQuery;
        } else {
          qName =
            this.gqlQuery.slice(-3) === "ies"
              ? this.gqlQuery.slice(0, -3) + "y"
              : this.gqlQuery.slice(0, -1);
        }

        query = gql`
          query ($id: ${this.primaryKeyType}!) {
            ${qName}(id: $id) {
              ${this.primaryKey}
              ${this.modelLabel}${
          this.modelFields ? "\n" + this.modelFields : ""
        }
            }
          }
        `;
      }

      try {
        const result = await this.$apollo.query({
          query,
          variables: {
            id
          }
        });

        return result.data[Object.keys(result.data)[0]];
      } catch (e) {
        return { id, name: "Auswahl nicht mehr verfügbar" };
      }
    },
    getOptionLabel(option) {
      if (typeof option === "object") {
        return this.selectLabel instanceof Function
          ? this.selectLabel(option)
          : option[this.selectLabel];
      }

      return option;
    },
    onInput(value) {
      if (this.multiple) {
        if (this.setLastElementNull) {
          value = this.multiple ? value.map(this.reduce) : this.reduce(value);
          this.$emit("input", value.length ? value : [null]);
        } else {
          this.$emit(
            "input",
            this.multiple ? value.map(this.reduce) : this.reduce(value)
          );
        }
      } else {
        const val = this.reduce(value);
        this.$emit("input", val ? val : this.allowNull ? null : "");
      }
    },
    withPopper(dropdownList, component, { width }) {
      dropdownList.style.width = width;

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, -1]
            }
          },
          {
            name: "toggleClass",
            enabled: true,
            phase: "write",
            fn({ state }) {
              component.$el.classList.toggle(
                "drop-up",
                state.placement === "top"
              );
            }
          }
        ]
      });

      return () => popper.destroy();
    }
  }
};
</script>

<style lang="scss">
.v-select.drop-up .vs__dropdown-toggle {
  border-radius: 0 0 4px 4px;
  border-top-style: none;
  border-bottom-style: solid;
  border-bottom-color: rgba(60, 60, 60, 0.26);
  box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15);
}
</style>
